import AbstractItemResource from "../abstract/abstract-item-resource"
import defines from "dpsdk/defines"
import Zone from './zone'
import resourceCollection from "../abstract/resource-collection"
import {formatDate, formatTime, humanDuration} from "../../utility/Utils"
import api from "../../api"
const {Resource, DeviceEnabledType, DeviceDoorStatus} = defines

class Device extends AbstractItemResource {
    constructor(data, parent) {
        super(Resource.DEVICES, data, parent)
        this.setLabels({
            id: 'ID'
        })

        this.zoneInstances = []
        if (Array.isArray(this.zones)) {
            this.zones.forEach((zone) => {
                this.zoneInstances.push(new Zone(zone))
            })
        }
    }


    fetchCameraImageHighQuality(source, forceNew = false) {
        const params = {
            high_quality: true,
            e: Date.now()
        }
        if (forceNew) {
            params.force = true
        }
        return api.objectUrlMeta(`/${Resource.DEVICES.toLowerCase()}/${this.id}/camera/${source}.jpg`, params)
    }

    fetchCameraLiveImage(source) {
        return api.objectUrl(`/${Resource.DEVICES.toLowerCase()}/${this.id}/camera/${source}.jpg`, {
            force: true,
            e: Date.now()
        })
    }

    fetchCameraLiveVideo(source, date, hour, minutes, duration) {
        const dateStr = `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}-${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}`
        return api.objectUrl(`/${Resource.DEVICES.toLowerCase()}/${this.id}/video/${source}`, {
            date: dateStr,
            hour,
            minutes,
            duration
        })
    }

    getFullTitle() {
        return `${this.title} [#${this.id}]`
    }

    getLocation() {
        return this.location
    }


    getNumberOfSources() {
        return this.video_size.split(',').length
    }

    getCameraSize(source) {
        const parts = this.video_size ? String(this.video_size).split(',') : []
        const item = parts[source] || parts[0] || ''
        const match = item.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/)
        if (match) {
            const width = parseInt(match[1], 10)
            const height = parseInt(match[2], 10)
            if (width > 0 && height > 0) {
                return {width, height}
            }
        }

        const detectionProfile = this.getDetectionProfile()
        if (detectionProfile && detectionProfile.image_width && detectionProfile.image_height) {
            return {
                width: parseInt(detectionProfile.image_width, 10),
                height: parseInt(detectionProfile.image_height, 10)
            }
        }

        return {width: 1920, height: 1080}
    }

    getZones() {
        return this.zoneInstances
    }

    isOnline() {
        return (this.server_time - this.ping_at) <= 60
    }

    checkTill() {
        if (!this.checked_at) {
            return 0
        }

        return this.checked_at + (this.check_duration * 365 * 24 * 3600) - (3600 * 24)
    }

    getVerificationStatusStr(t) {
        const checkTill = this.checkTill()
        const status = []
        if (this.checked_at) {
            status.push(<div key="checked-at">{formatDate(this.checked_at * 1000)}</div>)
        }

        if (checkTill === 0) {
            status.push(<div key="no-verification" className={'text-danger'}>{t('devices.noVerification')}</div>)
        } else if (checkTill < this.server_time) {
            status.push(<div key="validation-expired" className={'text-danger'}>{t('devices.validationExpired')}</div>)
        } else {
            if (checkTill < this.server_time + (3600 * 24 * 30 * 4)) {
                status.push(<div key="validation-ok" className={'text-warning'}>{t('devices.validationOk')}</div>)
            } else {
                status.push(<div key="validation-ok" className={'text-success'}>{t('devices.validationOk')}</div>)
            }
        }
        return status
    }

    getStatusStr(t) {
        const status = []

        if (this.isOnline()) {
            if (this.queue > 0) {
                status.push(<div key="queue" className='text-success'>{t('devices.statusReportsDelivery', {queue: this.queue})}</div>)
            } else {
                switch (this.getEnabled()) {
                    case DeviceEnabledType.ENABLED:
                        status.push(<div key="available" className="text-success">{t('devices.available')}</div>)
                        break
                    case DeviceEnabledType.MAINTENANCE:
                        status.push(<div key="maintenance" className="text-warning">{t('devices.maintenance')}</div>)
                        break
                    case DeviceEnabledType.DISABLED:
                        status.push(<div key="disabled" className="text-info">{t('devices.disabled')}</div>)
                        break
                    default:
                        status.push(null)
                        break
                }
            }

            if (this.alerts) {
                status.push(<div key="alerts" className="text-warning">{t('devices.errors')}</div>)
            }

            if (Number.isFinite(parseFloat(this.temperature)) && String(this.temperature).trim() !== '') {
                status.push(<div key="temperature" className="text-info">{t('devices.temp', {temperature: this.temperature})}</div>)
            }

            if (this.door_status === DeviceDoorStatus.CLOSED) {
                status.push(<div key="door" className="text-muted">{t('devices.doorOk')}</div>)
            } else if (this.door_status === DeviceDoorStatus.OPEN) {
                status.push(<div key="door" className="text-muted">{t('devices.doorOpened')}</div>)
            }
        } else {
            status.push(<div key="unavailable" className="text-danger">{t('devices.unavailable')}</div>)
        }

        if (this.isOnline()) {
            if (this.last_report_at > 0 && this.server_time - this.last_report_at > 3600) {
                status.push(<div key="last-report" className="text-danger">{t('devices.lastReportAt', {time: formatTime(new Date(this.last_report_at * 1000)), date: formatDate(new Date(this.last_report_at * 1000))})}</div>)
            }
        } else if (this.last_report_at === 0) {
            status.push(<div key="no-reports" className="text-danger">{t('devices.noReports')}</div>)
        }

        if (this.isOnline()) {
            if (this.started_at) {
                status.push(<div key="uptime" className='text-muted' style={{fontSize: "10px"}}>{t('devices.uninterrupted', {duration: humanDuration(this.server_time - this.started_at, t)})}</div>)
            }
        } else {
            if (this.disconnected_at || this.ping_at) {
                status.push(<div key="downtime" className="text-muted" style={{fontSize: "10px"}}>{t('devices.disabledSince', {duration: humanDuration(this.server_time - Math.max(this.disconnected_at, this.ping_at), t)})}</div>)
            }
        }
        return status
    }

    getDetectionProfile() {
        if (!this.detection_profile_id) {
            return null
        }

        return resourceCollection.getResource(Resource.DETECTION_PROFILES).findById(this.detection_profile_id)
    }

    getEnabled() {
        if (this.enabled !== DeviceEnabledType.PROFILE) {
            return this.enabled
        }
        const detectionProfile = this.getDetectionProfile()
        if (detectionProfile && detectionProfile.enabled !== undefined && detectionProfile.enabled !== null) {
            return detectionProfile.enabled
        }
        if (this.detection_profile_enabled !== undefined && this.detection_profile_enabled !== null && this.detection_profile_enabled !== '') {
            return Number(this.detection_profile_enabled)
        }
        return this.enabled
    }


    getViolationCodes() {
        const result = []
        this.getZones().forEach(zone => {
            const code = zone.codeStr()
            if (code && !result.includes(code)) {
                result.push(code)
            }
        })
        return result.join(', ')
    }
}

export default Device
